/**
 * PictureUp / CRM host split (see lib/host-split.ts).
 *
 * <PictureUpHostGate> wraps the routes. On PictureUp's own host it
 * serves only /aroll (and /sso), sends everything else to /aroll, and
 * hands a signed-in Kilogram account to the CRM with a short note rather
 * than silently opening the CRM there. Anywhere else it renders its
 * children untouched.
 *
 * <AccountHandoff> does the hand-off: shows the note, signs the person
 * in on the other origin through /sso, and signs this origin out (only
 * locally: the session itself moves, it is not revoked).
 */
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { clearTokens, getTokens, getUser } from '@/lib/auth-store';
import {
  HANDOFF_NOTES,
  currentHandoff,
  isPictureUpPath,
  onPictureUpHost,
  ssoHandoffUrl,
} from '@/lib/host-split';

type Handoff = NonNullable<ReturnType<typeof currentHandoff>>;

export function AccountHandoff({ handoff }: { handoff: Handoff }) {
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const tokens = getTokens();
      const user = getUser();
      const url =
        tokens && user
          ? ssoHandoffUrl(handoff.origin, tokens, user)
          : `${handoff.origin}${handoff.to === 'crm' ? '/' : '/aroll'}`;
      clearTokens();
      window.location.replace(url);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [handoff.origin, handoff.to]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6 text-center text-[15px] text-white/80">
      <p role="status">{HANDOFF_NOTES[handoff.note]}</p>
    </div>
  );
}

export function PictureUpHostGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  if (!onPictureUpHost()) return <>{children}</>;
  // A review link is public and works for anyone signed in or not, so it
  // never hands off.
  const isShare = location.pathname.startsWith('/aroll/s/');
  const handoff = isAuthenticated && !isShare ? currentHandoff(user) : null;
  if (handoff) return <AccountHandoff handoff={handoff} />;
  if (isPictureUpPath(location.pathname)) return <>{children}</>;
  return <Navigate to={`/aroll${location.search}`} replace />;
}
